import { AfterViewInit, Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DTypeFormAddressField } from '@grupo-constant/next-dtype/form';
import { AddressByGoogle, DTypeViewableCol } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { icon, LeafletMouseEvent, map, Map, Marker, marker, tileLayer } from 'leaflet';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { DTypeFormFieldComponent } from '../../dtype-field.model';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { LocationService } from '../../../location/location.service';
import { UserService } from '../../../user/user.service';
import { Utils } from '../../../utils/utils';

@Component({
    selector: 'address-field-component',
    templateUrl: './address-field.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DTypeAddressFieldComponent implements DTypeFormFieldComponent, OnInit, OnDestroy, AfterViewInit {

    @Input() field: DTypeFormAddressField;
    @Input() isUpdate: boolean;
    @Input() useSummaryView: boolean;

    nameFormControl = new FormControl('');
    ignoreCache = false;

    typeSelect: 'address' | 'location' | 'placeid' = 'address';

    formWithAddress = new FormGroup({
        address: new FormControl('', Validators.required)
    });

    formWithLocation = new FormGroup({
        latitude: new FormControl(null, Validators.required),
        longitude: new FormControl(null, Validators.required)
    });

    formWithPlaceId = new FormGroup({
        placeId: new FormControl('', Validators.required)
    });

    address: AddressByGoogle;
    mapId: string;

    private marker: Marker;
    private icon = icon({
        iconUrl: '/leaflet/images/marker-icon.png',
        shadowUrl: '/leaflet/images/marker-shadow.png',
        iconRetinaUrl: '/leaflet/images/marker-icon-2x.png',
        iconSize: [25, 41], // size of the icon
        shadowSize: [32, 32], // size of the shadow
        iconAnchor: [10, 40], // point of the icon which will correspond to marker's location
        shadowAnchor: [2, 30],  // the same for the shadow
    });
    private map: Map;

    private _unsubscribeAll = new Subject<void>();

    constructor(
        private dialogsService: DialogsService,
        private locationService: LocationService,
        private transloco: TranslocoService,
        private userService: UserService,
    ) { }

    ngOnInit() {
        this.address = this.field.control.value;
        if (!this.isUpdate) {
            this.nameFormControl.setValue('', { emitEvent: false });
        } else {
            this.nameFormControl.setValue(this.address['newName'], { emitEvent: false });
        }
        this.mapId = 'map' + Utils.generateRandomId();
        if (!this.address) {
            this.nameFormControl.disable();
        } else {
            this.formWithAddress.controls.address.setValue(this.address.vicinity);
            //Permitimos editar direcciones
            //this.formWithAddress.controls.address.disable();
        }
        this.nameFormControl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                this.address['newName'] = value;
                this.field.control.setValue(this.address);
        });

        const readonlyOnUpdate = (this.field.dTypeCol as DTypeViewableCol).readonlyOnUpdate;
        if (readonlyOnUpdate && this.isUpdate) {
            this.field.readonly = true;
        }
    }

    ngOnDestroy() {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    ngAfterViewInit() {
        if (!this.useSummaryView) {
            this.initMap();
        }
    }

    /**
     * Get addresses with the introduced text
     */
    async searchWithAddress() {
        const places = await firstValueFrom(this.locationService.retrieveGoogleAddress({
            completeAddress: this.formWithAddress.get('address').value,
            searchLanguage: this.userService.user?.preferredLanguage?.substring(0, 2),
            api: this.field.api,
            ignoreCache: this.ignoreCache
        }));
        this.fillAddress(places);
    }

    /**
     * Get addresses with the introduced coordinates or coordinates obtained by clicking the map
     *
     * @param latitude
     * @param longitude
     */
    async searchWithLocation(latitude?: number, longitude?: number) {
        const places = await firstValueFrom(this.locationService.retrieveGoogleAddress({
            latitude: latitude ?? this.formWithLocation.get('latitude').value,
            longitude: longitude ?? this.formWithLocation.get('longitude').value,
            keyword: this.field.keyword,
            api: this.field.api ?? 'PLACES',
            ignoreCache: this.ignoreCache
        }));
        this.fillAddress(places);
    }

    /**
     * Get addreseses with the introduced coordinates
     */
    async searchWithPlaceId() {
        const places = await firstValueFrom(this.locationService.retrieveGoogleAddress({
            placeId: this.formWithPlaceId.get('placeId').value
        }));
        this.fillAddress(places);
    }

    private initMap() {
        //Create new map centered on Madrid
        this.map = map(this.mapId, {
            center: [40.416877, -3.703526],
            zoom: 16,
            doubleClickZoom: false,
        });

        if (this.address) {
            this.centerMap(this.address.latitude, this.address.longitude);
            this.paintMarker(this.address.latitude, this.address.longitude);
        }

        //Set source of tiles
        const tiles = tileLayer(
            'https://maps.krama.es/hot/{z}/{x}/{y}.png',
            {
                maxZoom: 18,
                minZoom: 3,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            });

        // Add tile to map
        tiles.addTo(this.map);

        //Add click event handler
        if (this.field.canClickOnMap) {
            this.map.on('dblclick', (event: LeafletMouseEvent) => {
                // get map and coords
                const latLng = event.latlng;

                // get address
                this.searchWithLocation(latLng.lat, latLng.lng);
            });
        }
    }

    private fillAddress(places: AddressByGoogle[]) {
        if (places.length > 0) {
            if (places.length === 1) {
                const place = places[0];
                this.address = place;
                this.nameFormControl.setValue(this.address.name, { emitEvent: false });
                this.nameFormControl.enable();
                if (this.address.latitude && this.address.longitude) {
                    this.paintMarker(this.address.latitude, this.address.longitude);
                    this.centerMap(this.address.latitude, this.address.longitude);
                }
                this.field.control.setValue(this.address);
            } else {
                const dialog = this.dialogsService.openAddressListDialog(places);
                dialog.afterClosed().subscribe((result) => {
                    if (result) {
                        this.address = result;
                        this.nameFormControl.setValue(this.address.name, { emitEvent: false });
                        this.nameFormControl.enable();
                        if (this.address.latitude && this.address.longitude) {
                            this.paintMarker(this.address.latitude, this.address.longitude);
                            this.centerMap(this.address.latitude, this.address.longitude);
                        }
                        this.field.control.setValue(this.address);
                    }
                });
            }
        } else {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('noAddressFound')
            });
        }
    }

    private centerMap(lat: number, lng: number) {
        this.map.setView({ lat, lng }, this.map.getZoom(), { animate: true, duration: 1 });
    }

    private paintMarker(lat: number, lng: number) {
        if (this.marker) {
            this.map.removeLayer(this.marker);
        }
        this.marker = marker([lat, lng], { icon: this.icon });
        this.map.addLayer(this.marker);
    }

}
